import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { URLDevelopment } from "../helpers/URL";


const Home = () => {
  const [featured, setFeatured] = useState([]);
  const [trending, setTrending] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const [featuredRes, trendingRes, newRes] = await Promise.all([
          axios.get( `${URLDevelopment}/api/product/featured`),
          axios.get(`${URLDevelopment}/api/product/trending`),
          axios.get(`${URLDevelopment}/api/product/new`),
        ]);

        setFeatured(featuredRes.data);
        setTrending(trendingRes.data);
        setNewArrivals(newRes.data);
      } catch (err) {
        console.error("Home fetch error", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-blue-600 font-semibold">
        Loading products...
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
<section className="relative overflow-hidden bg-gradient-to-r from-blue-600 to-indigo-700 py-24 px-6 text-white">
  <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">

    {/* LEFT CONTENT */}
    <motion.div
      initial={{ opacity: 0, x: -60 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      <span className="inline-block mb-4 px-4 py-1 rounded-full bg-white/20 text-sm font-semibold tracking-wide">
        <span role="img" aria-label="fire">🔥</span> Limited Time Offer
      </span>

      <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-4">
        Big Deals on <br />
        <span className="text-yellow-300">Trending Products</span>
      </h1>

      <p className="text-lg text-white/90 mb-8 max-w-md">
        Upgrade your lifestyle with exclusive discounts on fashion,
        electronics, and more.
      </p>

      <div className="flex gap-4">
        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <Link
            to="/shop"
            className="bg-white text-blue-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
          >
            Shop Now
          </Link>
        </motion.div>

        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <Link
            to="/shop"
            className="border border-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-700 transition"
          >
            View Deals
          </Link>
        </motion.div>
      </div>
    </motion.div>

    {/* RIGHT IMAGE */}
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: "easeOut", delay: 0.2 }}
      className="hidden md:block"
    >
      <Link
  to={`/product/69d0c2b16451230168a812d3`}
  className="block"
>
      <img
        src={require('./../assets/hero.jpg')}
        alt="Featured products"
        className="w-full max-w-md mx-auto drop-shadow-2xl"
      />
      </Link>
    </motion.div>
  </div>

  {/* BACKGROUND DECOR */}
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 0.15 }}
    transition={{ duration: 1.2 }}
    className="absolute -top-24 -right-24 w-96 h-96 bg-white rounded-full blur-3xl"
  />
</section>



      {/* ================= TRENDING ================= */}
      <ProductSection
        title="Trending Products"
        subtitle="Most loved by customers"
        products={trending}
      />

      {/* ================= FEATURED / SALE ================= */}
      <ProductSection
        title="On Sale"
        subtitle="Limited time offers"
        products={featured}
        showSale
      />

      {/* ================= NEW ARRIVALS ================= */}
      <ProductSection
        title="New Arrivals"
        subtitle="Fresh products just for you"
        products={newArrivals}
        isNew
      />
    </div>
  );
};

/* ================= PRODUCT SECTION ================= */

const ProductSection = ({
  title,
  subtitle,
  products,
  showSale,
  isNew,
}) => (
  <section className="py-14 px-4">
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
        <p className="text-gray-500">{subtitle}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {products.map((product) => (
          <ProductCard
            key={product._id}
            product={product}
            showSale={showSale}
            isNew={isNew}
          />
        ))}
      </div>
    </div>
  </section>
);

/* ================= PRODUCT CARD ================= */

const ProductCard = ({ product, showSale, isNew }) => {
  const imageUrl = product.photo || "/no-image.png";

  return (
    <div className="bg-white rounded-xl shadow hover:shadow-lg transition overflow-hidden relative">
      {/* BADGES */}
      {product.isOnSale && (
        <span className="absolute top-3 left-3 bg-red-500 text-white text-xs px-2 py-1 rounded">
          Sale
        </span>
      )}

      {product.isFeatured && (
        <span className="absolute top-3 right-3 bg-green-500 text-white text-xs px-2 py-1 rounded">
          New
        </span>
      )}

      <img
        src={imageUrl}
        alt={product.name}
        className="w-full h-48 object-cover"
      />

      <div className="p-4">
        <h3 className="font-semibold text-gray-800 truncate">
          {product.name}
        </h3>

        <div className="mt-2">
          {product.finalPrice ? (
            <>
              <span className="text-red-600 font-bold mr-2">
                ₹{product.finalPrice}
              </span>
              <span className="text-gray-400 line-through text-sm">
                ₹{product.price}
              </span>
            </>
          ) : (
            <span className="text-blue-600 font-bold">
              ₹{product.price}
            </span>
          )}
        </div>

        <Link
          to={`/product/${product._id}`}
          className="block text-center mt-4 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition"
        >
          View Product
        </Link>
      </div>
    </div>
  );
};

export default Home;
